import { ref, computed } from 'vue'
import axios from 'axios'
import { authorizedUsersService } from '@/services/adminService.js'
import {
  authAuthorized,
  authDashboardAccess,
  authReady,
  authRole,
  authTeamIds,
  authUser,
  resetAuthState
} from './authState.js'

// Auth state is backed by the server-side HttpOnly session cookie. No Google
// token or user profile is persisted in browser storage.
const user = authUser
const token = ref(null)
const isLoading = ref(false)
const error = ref(null)
const isAuthorized = authAuthorized
const userRole = authRole
const hasDashboardAccess = authDashboardAccess
const userTeamIds = authTeamIds

// Development mode: skip authentication
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'

// Dev mode mock user (configurable via env)
const DEV_USER = {
  email: import.meta.env.VITE_DEV_USER_EMAIL || 'dev@frisseblikken.com',
  name: import.meta.env.VITE_DEV_USER_NAME || 'Development User',
  givenName: import.meta.env.VITE_DEV_USER_GIVEN_NAME || 'Development',
  familyName: import.meta.env.VITE_DEV_USER_FAMILY_NAME || 'User',
  picture: import.meta.env.VITE_DEV_USER_PICTURE || null,
  googleId: import.meta.env.VITE_DEV_USER_GOOGLE_ID || 'dev-user'
}

// Dev mode role (defaults to admin for full access)
const DEV_ROLE = import.meta.env.VITE_DEV_USER_ROLE || 'admin'

const apiBaseUrl = () => import.meta.env.VITE_API_URL || '/api'

function applyUser(userData) {
  user.value = userData
  userRole.value = userData?.role || null
  hasDashboardAccess.value = Boolean(userData?.dashboard_access ?? userData?.dashboardAccess)
}

function clearClientAuthState() {
  resetAuthState()
  token.value = null
  error.value = null
}

/**
 * Authentication composable for Google OAuth2 with a server-side session.
 */
export function useAuth() {
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => userRole.value === 'admin')
  const isCommerciant = computed(() => userRole.value === 'commerciant')
  const isPerformanceLead = computed(() => userRole.value === 'performance_lead')
  const isSourcingsbaas = computed(() => userRole.value === 'sourcingsbaas')

  const hasRole = (...roles) => roles.includes(userRole.value)

  /**
   * Check if the current user is authorized in the application database.
   */
  async function checkAuthorization(email) {
    try {
      const result = await authorizedUsersService.checkAuthorization(email)
      isAuthorized.value = result.authorized
      userRole.value = result.role
      hasDashboardAccess.value = !!result.dashboard_access
      userTeamIds.value = result.team_ids || []
      return result
    } catch (err) {
      console.error('Authorization check failed:', err)
      isAuthorized.value = false
      userRole.value = null
      hasDashboardAccess.value = false
      userTeamIds.value = []
      return { authorized: false, role: null, dashboard_access: false }
    }
  }

  /**
   * Restore the server-side session from the HttpOnly cookie.
   */
  async function initAuth() {
    authReady.value = false

    if (SKIP_AUTH) {
      applyUser(DEV_USER)
      isAuthorized.value = true
      userRole.value = DEV_ROLE
      hasDashboardAccess.value = true
      userTeamIds.value = []

      try {
        const result = await authorizedUsersService.checkAuthorization(DEV_USER.email)
        userTeamIds.value = result.team_ids || []
      } catch (_e) {
        // Dev user may not exist in the database yet.
      }
      authReady.value = true
      return
    }

    try {
      const currentUser = await verifyToken()
      if (currentUser?.email) {
        const authorization = await checkAuthorization(currentUser.email)
        if (!authorization.authorized) await logout()
      }
    } catch (err) {
      // An absent/expired cookie is a normal logged-out state.
      if (err.response?.status !== 401) {
        console.warn('Failed to restore server session:', err)
      }
      clearClientAuthState()
    } finally {
      authReady.value = true
    }
  }

  /**
   * Verify the application session with the backend.
   */
  async function verifyToken() {
    if (SKIP_AUTH) {
      applyUser(DEV_USER)
      return DEV_USER
    }

    const response = await axios.get(`${apiBaseUrl()}/auth/me`, {
      withCredentials: true,
      timeout: 10000
    })

    if (!response.data.success || !response.data.data) {
      throw new Error('Invalid session response')
    }

    applyUser(response.data.data)
    return response.data.data
  }

  /**
   * Complete Google login. The Google credential/code is sent only to the
   * backend login endpoint and is never stored in browser storage.
   */
  async function login(googleResponse) {
    isLoading.value = true
    error.value = null

    try {
      let response

      if (googleResponse.credential) {
        response = await axios.post(
          `${apiBaseUrl()}/auth/google/session`,
          { credential: googleResponse.credential },
          { withCredentials: true, timeout: 10000 }
        )
      } else if (googleResponse.code) {
        response = await axios.post(
          `${apiBaseUrl()}/auth/google/callback`,
          { code: googleResponse.code },
          { withCredentials: true, timeout: 10000 }
        )
      } else {
        throw new Error('Invalid Google response - no credential or code received')
      }

      if (!response.data.success || !response.data.data?.user) {
        throw new Error(response.data.error || 'Failed to create application session')
      }

      applyUser(response.data.data.user)
      const authorization = await checkAuthorization(response.data.data.user.email)
      if (!authorization.authorized) {
        await logout()
        throw new Error('User is not authorized to access this application')
      }

      isLoading.value = false
      return true
    } catch (err) {
      console.error('Login failed:', err)

      let errorMessage = 'Login failed. Please try again.'
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err.message) {
        errorMessage = err.message
      }

      error.value = errorMessage
      isLoading.value = false
      clearClientAuthState()
      throw new Error(errorMessage)
    }
  }

  /**
   * Revoke the server-side session and clear client-side UI state.
   */
  function logout() {
    const request = SKIP_AUTH
      ? Promise.resolve()
      : Promise.resolve(
          axios.post(`${apiBaseUrl()}/auth/logout`, {}, { withCredentials: true, timeout: 10000 })
        ).catch(err => {
          console.warn('Server logout failed:', err)
        })

    clearClientAuthState()
    return request
  }

  function isTokenExpired() {
    return !isAuthenticated.value
  }

  // Kept for compatibility with callers; bearer headers are intentionally no
  // longer generated because authentication uses the HttpOnly session cookie.
  function getAuthHeader() {
    return null
  }

  return {
    user,
    token,
    isAuthenticated,
    isAuthorized,
    userTeamIds,
    isAdmin,
    isCommerciant,
    isPerformanceLead,
    isSourcingsbaas,
    userRole,
    hasDashboardAccess,
    isLoading,
    error,
    isDevMode: SKIP_AUTH,
    initAuth,
    login,
    logout,
    verifyToken,
    checkAuthorization,
    isTokenExpired,
    getAuthHeader,
    hasRole
  }
}
